/**
 * Case Hooks
 * React Query hooks for case management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { casesApi } from '@/api'
import type { CaseUpdate, CaseFilters } from '@/types/case'
import type { PaginationParams } from '@/types/common'

// Query keys
export const caseKeys = {
  all: ['cases'] as const,
  lists: () => [...caseKeys.all, 'list'] as const,
  list: (filters?: CaseFilters, pagination?: PaginationParams) =>
    [...caseKeys.lists(), { filters, pagination }] as const,
  details: () => [...caseKeys.all, 'detail'] as const,
  detail: (id: string) => [...caseKeys.details(), id] as const,
  lawyers: (officeId?: number) => [...caseKeys.all, 'lawyers', officeId] as const,
}

/**
 * Get all lawyers for filter dropdown, optionally filtered by office
 */
export function useLawyers(officeId?: number) {
  return useQuery({
    queryKey: caseKeys.lawyers(officeId),
    queryFn: () => casesApi.getLawyers(officeId),
  })
}

/**
 * Get paginated list of cases
 */
export function useCases(filters?: CaseFilters, pagination?: PaginationParams) {
  return useQuery({
    queryKey: caseKeys.list(filters, pagination),
    queryFn: () => casesApi.getAll(filters, pagination),
  })
}

/**
 * Get single case by ID
 */
export function useCase(id: string) {
  return useQuery({
    queryKey: caseKeys.detail(id),
    queryFn: () => casesApi.getById(id),
    enabled: !!id,
  })
}

/**
 * Update existing case
 */
export function useUpdateCase() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CaseUpdate }) =>
      casesApi.update(id, data),
    onSuccess: (_updatedCase, variables) => {
      queryClient.invalidateQueries({ queryKey: caseKeys.lists() })
      queryClient.invalidateQueries({ queryKey: caseKeys.detail(variables.id) })
    },
  })
}
